import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Button, Spin } from "antd";
import { CheckOutlined } from "@ant-design/icons";
import { FaCheck } from "react-icons/fa";
import { RiDeleteBin6Line } from "react-icons/ri";
import dayjs from "dayjs";
import apiClient from "../../../helpers/apiClient";
import { useToast } from "../../../contexts/ToastContext";

const AxleInsuranceCard = ({
    reservation,
    disabled,
    isAxleVerifying,
    setIsAxleVerifying,
    onTokenData,
    removeVerification,
    onRemoveVerificationHandled,
    t,
}) => {
    const toast = useToast();
    const [isVerified, setIsVerified] = useState(false);
    const [showIframe, setShowIframe] = useState(false);
    const [ignitionUri, setIgnitionUri] = useState("");
    const [authCode, setAuthCode] = useState("");
    const [isFetchingPolicy, setIsFetchingPolicy] = useState(false);
    const [isActive, setIsActive] = useState(true);
    const [isExpiryBeforeDropOff, setIsExpiryBeforeDropOff] = useState(false);
    const [policyData, setPolicyData] = useState(null);
    const processedAuthCodeRef = useRef("");

    const handleRemove = useCallback(() => {
        setIsVerified(false);
        setPolicyData(null);
        setAuthCode("");
        processedAuthCodeRef.current = "";
        setShowIframe(false);
        setIsAxleVerifying(false);
        onTokenData(null);
    }, [onTokenData, setIsAxleVerifying]);

    useEffect(() => {
        const handleMessage = (message) => {
            switch (message.data.status) {
                case "complete":
                    setAuthCode(message.data.authCode);
                    setShowIframe(false);
                    break;
                case "exit":
                    toast.error({ message: t("axle.exitMessage") });
                    setShowIframe(false);
                    setIsAxleVerifying(false);
                    setIsVerified(false);
                    break;
                case "error":
                    toast.error({ message: t("axle.errorMessage") });
                    setShowIframe(false);
                    setIsAxleVerifying(false);
                    setIsVerified(false);
                    break;
                default:
                    break;
            }
        };

        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [toast, setIsAxleVerifying, t]);

    useEffect(() => {
        if (!authCode || processedAuthCodeRef.current === authCode) return;

        processedAuthCodeRef.current = authCode;
        setIsFetchingPolicy(true);

        (async () => {
            try {
                const tokenRes = await apiClient.post("/api/insurance/exchange-axle-token", { authCode });
                const tokenData = tokenRes?.data?.data;

                if (!tokenData?.accessToken) {
                    throw new Error("Missing access token");
                }

                const { data } = await apiClient.post("/api/insurance/get-axle-insurance-policies", {
                    accessToken: tokenData.accessToken,
                    policyId: tokenData.policies?.[0],
                });

                const policy = data?.data;
                const dropoff = dayjs(reservation?.exactDropoffTime || reservation?.dropoffDate);
                const expiry = policy?.expirationDate ? dayjs(policy.expirationDate) : null;
                const expiredBeforeDropoff = Boolean(
                    expiry?.isValid() && dropoff.isValid() && expiry.isBefore(dropoff),
                );
                const policyIsActive = policy?.isActive !== false;

                setPolicyData(policy);
                setIsVerified(true);
                setIsActive(policyIsActive);
                setIsExpiryBeforeDropOff(expiredBeforeDropoff);

                if (policyIsActive && !expiredBeforeDropoff) {
                    toast.success({ message: t("axle.successMessage") });
                    onTokenData(tokenData);
                } else {
                    onTokenData(null);
                }
            } catch {
                toast.error({ message: t("axle.errorMessage") });
                handleRemove();
            } finally {
                setIsFetchingPolicy(false);
                setIsAxleVerifying(false);
            }
        })();
    }, [
        authCode,
        handleRemove, toast,
        onTokenData,
        reservation?.dropoffDate,
        reservation?.exactDropoffTime,
        setIsAxleVerifying,
        t,
    ]);

    useEffect(() => {
        if (removeVerification) {
            handleRemove();
            onRemoveVerificationHandled?.();
        }
    }, [handleRemove, onRemoveVerificationHandled, removeVerification]);

    const handleVerify = async () => {
        if (!reservation?.userUID && !reservation?.email) {
            toast.error({ message: t("axle.errorMessage") });
            return;
        }

        setIsAxleVerifying(true);
        try {
            const user = {
                id: reservation.userUID || reservation.userId,
                firstName: reservation.firstName,
                lastName: reservation.lastName,
                email: reservation.email,
                phone: reservation.phoneNumber || reservation.mobileNumber,
            };
            const { data } = await apiClient.post("/api/insurance/get-axle-ignition-token", { user });
            setIgnitionUri(data.data.ignitionUri);
            setShowIframe(true);
        } catch {
            toast.error({ message: t("axle.errorMessage") });
            setIsAxleVerifying(false);
            setIsVerified(false);
            setShowIframe(false);
        }
    };

    const isSuccess = isVerified && isActive && !isExpiryBeforeDropOff;
    const coverages = policyData?.coverages?.map((coverage) => ({ text: coverage.label })) ?? [];

    return (
        <>
            <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="fontRegular text-sm text-[#5F6673]">{t("axle.description")}</p>
                <div className="flex shrink-0 items-center gap-2">
                    {isVerified ? (
                        <button
                            type="button"
                            onClick={handleRemove}
                            disabled={disabled}
                            className="text-red-600 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-50">
                            <RiDeleteBin6Line size={18} />
                        </button>
                    ) : null}
                    <Button
                        type={isSuccess ? "default" : "primary"}
                        loading={isAxleVerifying}
                        disabled={disabled || isVerified}
                        onClick={handleVerify}
                        icon={isSuccess ? <CheckOutlined /> : undefined}
                        className={
                            isSuccess ? "!border-[#16A34A] !bg-[#16A34A] !text-white hover:!bg-[#15803d]" : ""
                        }>
                        {isAxleVerifying ? null : isSuccess ? t("axle.verified") : t("axle.verify")}
                    </Button>
                </div>
            </div>

            {isFetchingPolicy ? (
                <div className="mt-2 flex items-center gap-2 text-sm text-[#7F8796]">
                    <Spin size="small" />
                    <span>{t("axle.loadingPolicy")}</span>
                </div>
            ) : null}

            {isVerified && (!isActive || isExpiryBeforeDropOff) ? (
                <Alert
                    type="error"
                    showIcon
                    className="mt-2"
                    message={!isActive ? t("axle.policyInactive") : t("axle.policyExpired")}
                    description={isExpiryBeforeDropOff ? t("axle.policyExpiredDesc") : undefined}
                />
            ) : null}

            {/* ID-card verified policies have no coverages, but carrier and policy number are still useful */}
            {isSuccess && (coverages.length > 0 || policyData?.carrier) ? (
                <ul className="mt-3 space-y-1.5">
                    {policyData?.carrier ? (
                        <li className="fontSemi text-sm capitalize text-[#5F6673]">
                            {t("axle.carrier")}: {String(policyData.carrier).replace("-", " ")}
                            {policyData.policyNumber ? (
                                <span className="fontRegular ml-2 text-[#98A2B3]">#{policyData.policyNumber}</span>
                            ) : null}
                        </li>
                    ) : null}
                    {coverages.map((coverage) => (
                        <li key={coverage.text} className="fontRegular flex items-start gap-2 text-sm text-[#5F6673]">
                            <span className="mt-0.5 shrink-0">
                                <FaCheck className="text-[#16A34A]" size={11} />
                            </span>
                            <span>{coverage.text}</span>
                        </li>
                    ))}
                </ul>
            ) : null}

            {showIframe && ignitionUri ? (
                <iframe
                    title={t("axle.verify")}
                    src={`${ignitionUri}&origin=${window.location.origin}`}
                    className="fixed left-0 top-0 z-[999] h-full w-full border-none"
                />
            ) : null}
        </>
    );
};

AxleInsuranceCard.propTypes = {
    reservation: PropTypes.object,
    disabled: PropTypes.bool,
    isAxleVerifying: PropTypes.bool.isRequired,
    setIsAxleVerifying: PropTypes.func.isRequired,
    onTokenData: PropTypes.func.isRequired,
    removeVerification: PropTypes.bool,
    onRemoveVerificationHandled: PropTypes.func,
    t: PropTypes.func.isRequired,
};

export default AxleInsuranceCard;
